import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { getMonthDay } from '@/lib/utils';

export interface BadgeState {
    activityList: ListWrap<Badge.Activity> & ListReq;
    /** 通用勋章 */
    commonBadges: {
        count: number,
        list: Badge.ListItem[],
    };
    finalMedals: {
        count: number,
        list: Array<IdName & {image: string}>,
    };
    /** 碎片勋章 */
    fragmentBadges: {
        count: number,
        list: Badge.ListItem[],
    };
    holidayMedals: {
        count: number,
        list: Badge.ListItem[],
    };
    /** 活动相关勋章 */
    activityBadges: {
        [activityId: string]: Medal.ActivityMedalGroup[] | undefined;
    };
    /** 点亮勋章卡的数量 */
    lightBagesCount: number;
    /** 勋章卡信息 */
    badgeDetails: {
        [cardId: string]: Badge .DetailsInfo,
    };
    /** 我的数据 */
    myData: ListWrap<Badge.MyData> & ListRequesting;
    received: {
        [userId: number]: ListWrap<Badge.ReceivedItem> & ListRequesting & { total: number };
    };
    today: Badge.DetailsInfo[] | null;
}

const m: Module<BadgeState, RootState> = {
    namespaced: true,
    state: () => ({
        activityList: {
            list: [],
            loaded: false,
            req: false,
        },
        commonBadges: {
            list: [],
            count: 0,
        },
        finalMedals: {
            list: [],
            count: 0,
        },
        fragmentBadges: {
            list: [],
            count: 0,
        },
        holidayMedals: {
            list: [],
            count: 0,
        },
        activityBadges: {},
        badgeDetails: {},
        lightBagesCount: 0,
        myData: {
            requesting: false,
            loaded: false,
            list: [],
        },
        received: {},
        today: null,
    }),
    mutations: {
        concatActivityList(state, { key, list }) {
            state.activityList.list = state.activityList.list.concat(list);
        },
        setActivityListLoaded(state, { key, loaded }) {
            state.activityList.loaded = loaded;
        },
        setActivityListReq(state, { key, req }) {
            state.activityList.req = req;
        },
        setCommonBadges(state, list: Badge.ListItem[]) {
            let count: number = 0;
            list.forEach((item) => {
                (item.medals || []).forEach((item) => {
                    if (item.received) {
                        count ++;
                    }
                });
                item.unfold = false;
            });
            Vue.set(state.commonBadges, 'list', list);
            Vue.set(state.commonBadges, 'count', count);
        },
        setFragmentBadges(state, list: Badge.ListItem[]) {
            let count: number = 0;
            list.forEach((item) => {
                (item.medals || []).forEach((item) => {
                    if (item.received) {
                        count ++;
                    }
                });
                item.unfold = false;
            });
            Vue.set(state.fragmentBadges, 'list', list);
            Vue.set(state.fragmentBadges, 'count', count);
        },
        setHolidayMedals(state, list: Badge.ListItem[]) {
            let count: number = 0;
            list.forEach((item) => {
                (item.medals || []).forEach((item) => {
                    if (item.received) {
                        count ++;
                    }
                });
                item.unfold = false;
            });
            Vue.set(state.holidayMedals, 'list', list);
            Vue.set(state.holidayMedals, 'count', count);
        },
        setFinalMedal(state, list: Array<IdName & {image: string}>) {
            // let count: number = 0;
            // list.forEach((item) => {
            //     count++;
            //     // (item.medals || []).forEach((item) => {
            //     //     if (item.received) {
            //     //         count ++;
            //     //     }
            //     // });
            //     // item.unfold = false;
            // });
            Vue.set(state.finalMedals, 'list', list);
            Vue.set(state.finalMedals, 'count', list.length);
        },
        setActivityBadges(state, { activityId, data }) {
            Vue.set(state.activityBadges, activityId, data);
        },
        /** 设置勋章卡详情 */
        setDetail(state, { medalId, detail }) {
            Vue.set(state.badgeDetails, medalId, detail);
        },
        setMyDataRequest(state, request) {
            state.myData.requesting = request;
        },
        setMyDataLoaded(state, loaded) {
            state.myData.loaded = loaded;
        },
        concatMyData(state, list) {
            state.myData.list = state.myData.list.concat(list);
        },
        /**
         * 已获得勋章
         */
        initReceived(state, { userId }) {
            Vue.set(state.received, userId, {
                list: [],
                loaded: false,
                requesting: false,
                total: 0,
            });
        },
        concatReceived(state, { userId, list }) {
            state.received[userId].list.push(...list);
        },
        setReceivedTotal(state, { userId, total }) {
            state.received[userId].total = total;
        },
        setReceivedLoaded(state, { userId, loaded }) {
            state.received[userId].loaded = loaded;
        },
        setReceivedReq(state, { userId, req }) {
            state.received[userId].requesting = req;
        },
        /**
         * 记录当日特殊勋章
         */
        setToday(state, today) {
            state.today = today;
        },
    },
    actions: {
        /**
         * 获取活动相关勋章
         */
        async getActivityList({ state, commit }, { offset, count }) {
            const wrap = state.activityList;
            if (wrap.req || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setActivityListReq', { req: true });
            const response = await Vue.axios.get('/api/v1/medal/activity_list', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatActivityList', { list });
                if (list.length < count) {
                    commit('setActivityListLoaded', { loaded: true });
                }
            }
            commit('setActivityListReq', { req: false });
            return rdata;
        },
        /**
         * 获取活动相关勋章
         */
        async getActivityBadges({ state, commit }, { activityId, userId }: { activityId: string, userId: number }) {
            const response = await Vue.axios.get('/api/v1/medal/activity', {
                params: {
                    id: activityId,
                    user_id: userId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setActivityBadges', {
                    activityId,
                    data: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取通用勋章
         */
        async getCommonBadges({ state, commit }, userId?) {
            const response = await Vue.axios.get('/api/v1/medal/common', {
                params: {
                    user_id: userId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setCommonBadges', rdata.data);
            }
            return rdata;
        },
        /**
         * 获取碎片勋章
         */
        async getFragmentBadges({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/medal/fragment');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setFragmentBadges', rdata.data);
            }
            return rdata;
        },
        /**
         * 获取碎片勋章
         */
        async getHolidayMedals({ state, commit }, year) {
            const response = await Vue.axios.get('/api/v1/medal/holiday_medal', {
                params: {
                    offset: 0,
                    count: 300,
                    year
                }
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setHolidayMedals', rdata.data);
            }
            return rdata;
        },
        async getFinalMedal({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/medal/final_medal');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setFinalMedal', rdata.data);
            }
            return rdata;
        },
        /**
         * 获取勋章卡详情
         */
        async getDetail({ state, commit }, { medalId, userId }) {
            const response = await Vue.axios.get('/api/v1/medal/detail', {
                params: {
                    id: medalId,
                    user_id: userId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setDetail', {
                    medalId,
                    detail: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取我的数据
         */
        async getMyData({ state, commit }, { count, offset, forceUpdate }: { count: number; offset: number; forceUpdate: boolean }) {
            if (state.myData.requesting || (state.myData.loaded && !forceUpdate)) {
                return;
            }
            if (offset === 0 && forceUpdate) {
                state.myData.list = [];
                state.myData.loaded = false;
                state.myData.requesting = false;
            }
            const end = offset + count;
            if (end <= state.myData.list.length) {
                return;
            }
            if (offset < state.myData.list.length) {
                offset = state.myData.list.length;
                count = end - offset;
            }
            commit('setMyDataRequest', true);
            const response = await Vue.axios.get('/api/v1/user/week_card', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatMyData', rdata.data);
                if (rdata.data.length < count) {
                    commit('setMyDataLoaded', true);
                }
            }
            commit('setMyDataRequest', false);
            return rdata;
        },
        async getReceived({ state, commit }, { userId, offset, count }) {
            let wrap = state.received[userId];
            if (!wrap) {
                commit('initReceived', { userId });
                wrap = state.received[userId];
            }
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            if (end <= state.myData.list.length) {
                return;
            }
            if (offset < state.myData.list.length) {
                offset = state.myData.list.length;
                count = end - offset;
            }
            commit('setReceivedReq', { userId, req: true });
            const response = await Vue.axios.get('/api/v1/medal/user_medal', {
                params: {
                    user_id: userId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data.list;
                commit('concatReceived', { userId, list });
                commit('setReceivedTotal', { userId, total: rdata.data.total });
                if (list.length < count) {
                    commit('setReceivedLoaded', { userId, loaded: true });
                }
            }
            commit('setReceivedReq', { userId, req: false });
            return rdata;
        },
        /**
         * 获取当天数据
         */
        async getToday(context) {
            const response = await Vue.axios.get('/api/v1/medal/today_medal');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setToday', rdata.data);
            }
            return rdata;
        },
    },
};
export default m;
